import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { LineChart, Line, XAxis, YAxis, ReferenceLine, ResponsiveContainer, Area, ComposedChart, Tooltip } from 'recharts';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import {
  BarChart3, Settings, RefreshCw, TrendingUp, TrendingDown, Minus,
  ChevronRight, Download, Globe, Phone, DollarSign, Users, Target,
  Percent, X, ArrowUpRight, ArrowDownRight, HelpCircle, ExternalLink, Check, Link2, Copy, GripVertical, Edit3, Save, Loader2, Pencil
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PacingDashboardGrid from '../../components/marketing/PacingDashboardGrid';
import MonthlyTargetsEditor from '../../components/marketing/MonthlyTargetsEditor';
// Department Scorecard Components
import { DepartmentScorecardSection } from '../../components/marketing/L10ScorecardSection';
import WeeklyRequiredOutput from '../../components/marketing/WeeklyRequiredOutput';
import MarketingEfficiencyBlock from '../../components/marketing/MarketingEfficiencyBlock';
import MarketPerformanceBreakdown from '../../components/marketing/MarketPerformanceBreakdown';
import EnhancedWeeklyPacingGrid from '../../components/marketing/EnhancedWeeklyPacingGrid';
import MetricsTrueUpModal from '../../components/modals/MetricsTrueUpModal';
// Draggable Sections
import DraggableSections, { 
  DEFAULT_SECTION_ORDER, 
  getSavedSectionOrder, 
  saveSectionOrder 
} from '../../components/marketing/DraggableSections';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// =====================================
// STATUS HELPERS
// =====================================
const statusColors = {
  // 3-tier status system: on_pace (green), at_risk (yellow), below_min (red)
  on_pace: {
    bg: 'bg-green-100 dark:bg-green-900/20',
    border: 'border-green-500',
    text: 'text-green-700 dark:text-green-400',
    badge: 'bg-green-500'
  },
  at_risk: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/20',
    border: 'border-yellow-500',
    text: 'text-yellow-700 dark:text-yellow-400',
    badge: 'bg-yellow-500'
  },
  below_min: {
    bg: 'bg-red-100 dark:bg-red-900/20',
    border: 'border-red-500',
    text: 'text-red-700 dark:text-red-400',
    badge: 'bg-red-500'
  },
  // Legacy mappings for backwards compatibility
  green: {
    bg: 'bg-green-100 dark:bg-green-900/20',
    border: 'border-green-500',
    text: 'text-green-700 dark:text-green-400',
    badge: 'bg-green-500'
  },
  yellow: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/20',
    border: 'border-yellow-500',
    text: 'text-yellow-700 dark:text-yellow-400',
    badge: 'bg-yellow-500'
  },
  red: {
    bg: 'bg-red-100 dark:bg-red-900/20',
    border: 'border-red-500',
    text: 'text-red-700 dark:text-red-400',
    badge: 'bg-red-500'
  }
};

const statusLabels = {
  on_pace: 'Goal Met',
  at_risk: 'Needs Attention',
  below_min: 'Below Minimum',
  green: 'Goal Met',
  yellow: 'Needs Attention',
  red: 'Below Minimum'
};

// =====================================
// FORMAT HELPERS
// =====================================
const formatCurrency = (value, decimals = 0) => {
  if (value === undefined || value === null) return '$0';
  const num = Number(value);
  if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(decimals > 0 ? 1 : 0)}K`;
  return `$${num.toFixed(decimals)}`;
};

// Currency with 2 decimals for revenue
const formatRevenue = (value) => {
  if (value === undefined || value === null) return '$0.00';
  const num = Number(value);
  if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
};

const formatNumber = (value) => {
  if (value === undefined || value === null) return '0';
  const num = Number(value);
  // Use Math.round for proper rounding, not truncation
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return Math.round(num).toString();
};

const formatPercent = (value) => {
  if (value === undefined || value === null) return '0%';
  return `${Number(value).toFixed(1)}%`;
};

const formatMultiple = (value) => {
  if (value === undefined || value === null) return '0x';
  return `${Number(value).toFixed(1)}x`;
};

// =====================================
// KPI TOOLTIPS - Leadership-friendly explanations
// =====================================
const kpiTooltips = {
  leads: "Total new opportunities/leads generated from all marketing channels this period",
  appointments_set: "Appointments scheduled with potential customers",
  issued: "Leads that have been issued/assigned for follow-up",
  demos: "Demos/presentations conducted with potential customers",
  sales: "Total number of contracts/jobs sold this period",
  payments_received: "Number of payments received from customers",
  revenue: "Total contract value from closed sales (Sold Jobs Amount)",
  revenue_collected: "Total revenue collected from payments received (Revenue Amount)",
  spend: "Total marketing spend across all channels and agencies",
  cpl: "Cost Per Lead - How much we pay to acquire each lead (Spend ÷ Leads)",
  cpas: "Cost Per Appointment Set - Cost to get each appointment on the calendar (Spend ÷ Appointments)",
  roas: "Return on Ad Spend - Revenue generated per dollar spent on marketing (Revenue ÷ Spend)",
  set_rate: "Percentage of leads that convert to scheduled appointments (Appointments ÷ Leads × 100)",
  demo_rate: "Percentage of appointments that result in a demo/presentation (Demos ÷ Appointments × 100)",
  close_rate: "Percentage of demos that convert to closed sales (Sales ÷ Demos × 100)"
};

const pacingExplanation = "Pacing is calculated based on elapsed time in the period. If we're 30% through the month and have 35% of our goal, we're ahead of pace.";

// =====================================
// MINI PROGRESS CHART - Shows progress toward EOM target
// =====================================
const MiniProgressChart = ({ weeklyData = [], metricKey, target, actual, daysElapsed = 26, totalDays = 28, format = 'number' }) => {
  // Map metric keys to weekly data keys
  const weeklyKeyMap = {
    'leads': 'leads',
    'appointments': 'appointments',
    'appointments_set': 'appointments',
    'issued': 'leads', // Use leads as proxy
    'demos': 'demos',
    'sales': 'sales',
    'payments_received': 'sales', // Use sales as proxy
    'revenue': 'revenue',
    'revenue_collected': 'revenue',
    'spend': 'spend',
    'cpl': 'cpl',
    'roas': 'roas'
  };
  
  const dataKey = weeklyKeyMap[metricKey] || metricKey;
  
  // Format value based on metric type
  const formatTooltipValue = (val) => {
    if (val === null || val === undefined) return '-';
    if (format === 'currency') {
      return val >= 1000000 ? `$${(val / 1000000).toFixed(2)}M` : 
             val >= 1000 ? `$${(val / 1000).toFixed(1)}K` : `$${Math.round(val)}`;
    }
    if (format === 'multiple') return `${val.toFixed(2)}x`;
    return Math.round(val).toLocaleString();
  };
  
  // Build cumulative data points from weekly data
  const chartData = useMemo(() => {
    if (!weeklyData || weeklyData.length === 0 || !target) return [];
    
    const points = [];
    let cumulative = 0;
    
    // Calculate total from weekly data vs actual (true-up) value
    const weeklyTotal = weeklyData.reduce((sum, week) => sum + (week[dataKey] || 0), 0);
    const scaleFactor = (actual && weeklyTotal > 0) ? actual / weeklyTotal : 1;
    
    // Add starting point at day 0
    points.push({ day: 0, actual: 0, target: 0, weekLabel: 'Start' });
    
    // Process each week - scale to match true-up if needed
    weeklyData.forEach((week, index) => {
      const weekValue = week[dataKey] || 0;
      cumulative += weekValue;
      
      // Scale cumulative to match true-up actual value
      const scaledCumulative = Math.round(cumulative * scaleFactor);
      
      // Calculate day number (end of week)
      const dayNum = Math.min((index + 1) * 7, totalDays);
      const targetAtDay = (target / totalDays) * dayNum;
      
      points.push({
        day: dayNum,
        actual: scaledCumulative,
        target: Math.round(targetAtDay),
        weekLabel: `Week ${index + 1}`
      });
    });
    
    // Ensure last point matches exact actual value
    if (actual && points.length > 1) {
      points[points.length - 1].actual = actual;
    }
    
    // Add end-of-month target point
    if (points.length > 0 && points[points.length - 1].day < totalDays) {
      points.push({
        day: totalDays,
        actual: null, // No actual data yet
        target: target,
        weekLabel: 'EOM Target'
      });
    }
    
    return points;
  }, [weeklyData, dataKey, target, actual, totalDays]);
  
  if (chartData.length < 2 || !target) return null;
  
  // Determine line color based on actual vs target pace
  const lastActualPoint = chartData.filter(p => p.actual !== null).pop();
  const isAhead = lastActualPoint && lastActualPoint.actual >= lastActualPoint.target;
  const lineColor = isAhead ? '#22c55e' : '#f59e0b'; // green or amber
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    
    const dataPoint = payload[0]?.payload;
    const actualVal = dataPoint?.actual;
    const targetVal = dataPoint?.target;
    const weekLabel = dataPoint?.weekLabel || `Day ${label}`;
    
    // Calculate percentage difference relative to target
    const diff = actualVal !== null && targetVal ? actualVal - targetVal : 0;
    const pctDiff = targetVal > 0 ? Math.abs((diff / targetVal) * 100).toFixed(1) : 0;
    
    return (
      <div className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs shadow-lg">
        <div className="font-medium text-white mb-1">{weekLabel}</div>
        {actualVal !== null && (
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: lineColor }} />
            <span className="text-slate-300">Actual:</span>
            <span className="text-white font-medium">{formatTooltipValue(actualVal)}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-slate-500" />
          <span className="text-slate-300">Pace:</span>
          <span className="text-white font-medium">{formatTooltipValue(targetVal)}</span>
        </div>
        {actualVal !== null && targetVal > 0 && (
          <div className={`mt-1 pt-1 border-t border-slate-700 ${actualVal >= targetVal ? 'text-green-400' : 'text-amber-400'}`}>
            {actualVal >= targetVal ? '↑' : '↓'} {formatTooltipValue(Math.abs(diff))} ({pctDiff}%) {actualVal >= targetVal ? 'ahead' : 'behind'}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="mt-2 h-12 w-full" style={{ minWidth: '100px', minHeight: '48px' }}>
      <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={48}>
        <ComposedChart data={chartData} margin={{ top: 2, right: 4, bottom: 2, left: 4 }}>
          <Tooltip content={<CustomTooltip />} />
          {/* Target pace line (dashed) */}
          <Line
            type="linear"
            dataKey="target"
            stroke="#64748b"
            strokeWidth={1}
            strokeDasharray="3 3"
            dot={false}
            isAnimationActive={false}
          />
          {/* Actual progress line */}
          <Line
            type="monotone"
            dataKey="actual"
            stroke={lineColor}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            connectNulls={false}
          />
          {/* Current day marker */}
          <ReferenceLine 
            x={daysElapsed} 
            stroke="#3b82f6" 
            strokeWidth={1} 
            strokeDasharray="2 2"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// =====================================
// EDITABLE KPI CARD COMPONENT
// =====================================
const EditableKPICard = ({ 
  label, 
  metricKey, 
  kpi, 
  format = 'number', 
  tooltip, 
  markets = [],
  period = 'mtd',
  onSaved,
  isEditable = true,
  weeklyData = [],
  daysElapsed = 26,
  totalDays = 28
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingValues, setLoadingValues] = useState(false);
  const [marketValues, setMarketValues] = useState({});
  const [activeTab, setActiveTab] = useState('markets'); // Default to markets tab
  
  // Map display keys to backend override keys
  const backendKeyMap = {
    'leads': 'leads',
    'appointments': 'appointments_set',
    'appointments_set': 'appointments_set',
    'issued': 'leads_issued',
    'demos': 'demos',
    'sales': 'jobs_sold',
    'payments_received': 'payments_received',
    'revenue': 'sold_jobs_amount',
    'revenue_collected': 'revenue_amount',
    'spend': 'ad_spend',
    'cpl': 'cost_per_lead',
    'roas': 'roas'
  };
  
  const backendKey = backendKeyMap[metricKey] || metricKey;
  
  // Load existing values when popover opens
  useEffect(() => {
    if (isOpen && markets.length > 0) {
      loadMarketValues();
    }
  }, [isOpen, markets]);
  
  const loadMarketValues = async () => {
    setLoadingValues(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const values = {};
      
      for (const market of markets) {
        try {
          const response = await axios.get(
            `${API}/marketing/pacing/true-up?period=${period}&market=${market.slug}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data?.overrides?.[backendKey] !== undefined) {
            values[market.slug] = response.data.overrides[backendKey];
          }
        } catch (e) {
          // Market might not have overrides
        }
      }
      setMarketValues(values);
    } catch (error) {
      console.error('Failed to load market values:', error);
    } finally {
      setLoadingValues(false);
    }
  };
  
  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      // Save each market's value
      for (const [marketSlug, value] of Object.entries(marketValues)) {
        if (value === undefined || value === '') continue;
        
        const payload = {
          period,
          market: marketSlug,
          [backendKey]: format === 'currency' ? parseFloat(value) : parseInt(value)
        };
        
        await axios.post(`${API}/marketing/pacing/true-up`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success(`${label} updated for all markets`);
      setIsOpen(false);
      onSaved?.();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save values');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarketValueChange = (marketSlug, value) => {
    setMarketValues(prev => ({
      ...prev,
      [marketSlug]: value
    }));
  };
  
  // Calculate company total from market values
  const companyTotal = Object.values(marketValues).reduce((sum, val) => {
    const num = parseFloat(val) || 0;
    return sum + num;
  }, 0);
  
  if (!kpi) return null;
  
  const status = kpi.status || 'on_pace';
  const colors = statusColors[status] || statusColors.on_pace;
  
  const formatValue = (val) => {
    if (format === 'currency') return formatCurrency(val);
    if (format === 'percent') return formatPercent(val);
    if (format === 'multiple') return formatMultiple(val);
    return formatNumber(val);
  };
  
  const TrendIcon = kpi.trend === 'up' ? TrendingUp : kpi.trend === 'down' ? TrendingDown : Minus;
  
  const getStatusDescription = () => {
    if (status === 'on_pace') return 'Meeting or exceeding target';
    if (status === 'at_risk') return 'Above minimum but below target';
    if (status === 'below_min') return 'Below minimum threshold';
    return statusLabels[status] || 'Unknown';
  };
  
  // Non-editable metrics (only percentage rates are non-editable)
  const nonEditableKeys = ['set_rate', 'demo_rate', 'close_rate', 'cpa', 'cpas', 'average_ticket'];
  const canEdit = isEditable && !nonEditableKeys.includes(metricKey);

  const CardContent = (
    <div
      data-testid={`kpi-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
      title={tooltip}
      className={`
        flex-shrink-0 min-w-[180px] p-4 rounded-lg border-l-4 transition-all relative group
        ${colors.border} ${colors.bg}
        ${canEdit ? 'cursor-pointer hover:scale-[1.02]' : ''}
      `}
    >
      {/* Edit indicator */}
      {canEdit && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Pencil className="h-3 w-3 text-muted-foreground" />
        </div>
      )}
      
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
        {label}
      </div>
      <div className="text-2xl font-bold dark:text-white">
        {formatValue(kpi.actual)}
      </div>
      
      {/* End-of-Month Projection - Leadership View */}
      {kpi.pace_projection !== undefined && kpi.target > 0 && (
        <div className="mt-2 p-2 rounded bg-slate-100 dark:bg-slate-800/50">
          <div className="text-xs text-muted-foreground">Projected EOM</div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-semibold">{formatValue(format === 'currency' ? Math.round(kpi.pace_projection * 100) / 100 : Math.round(kpi.pace_projection))}</span>
            <span className="text-xs text-muted-foreground">vs {formatValue(kpi.target)} target</span>
          </div>
          <div className={`text-xs mt-1 ${kpi.gap_to_target >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {kpi.gap_to_target >= 0 ? '↑' : '↓'} {Math.abs(Math.round(kpi.gap_to_target))} {kpi.gap_to_target >= 0 ? 'above' : 'below'} target
          </div>
          
          {/* Mini Progress Chart */}
          <MiniProgressChart
            weeklyData={weeklyData}
            metricKey={metricKey}
            target={kpi.target}
            actual={kpi.actual}
            daysElapsed={daysElapsed}
            totalDays={totalDays}
            format={format}
          />
        </div>
      )}
      
      <div className="flex items-center gap-2 mt-2">
        <span className={`w-2 h-2 rounded-full ${colors.badge}`} />
        <span className={`text-xs ${colors.text}`} title={getStatusDescription()}>
          {statusLabels[status] || status}
        </span>
      </div>
    </div>
  );
  
  if (!canEdit) {
    return CardContent;
  }
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {CardContent}
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0 z-50" 
        align="start" 
        side="bottom" 
        sideOffset={8}
        avoidCollisions={false}
      >
        <div className="p-4 border-b dark:border-slate-700">
          <h3 className="font-semibold flex items-center gap-2">
            <Edit3 className="h-4 w-4 text-primary" />
            Edit {label}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Enter values by market. Company total is auto-calculated.
          </p>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full rounded-none border-b dark:border-slate-700">
            <TabsTrigger value="company" className="flex-1 text-xs">
              Company Total
            </TabsTrigger>
            <TabsTrigger value="markets" className="flex-1 text-xs">
              By Market
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="company" className="p-4 pt-3 space-y-3">
            <div className="text-center py-4">
              <div className="text-3xl font-bold text-primary">
                {format === 'currency' ? `$${companyTotal.toLocaleString()}` : companyTotal.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Sum of all market values
              </p>
            </div>
            <div className="text-xs text-muted-foreground bg-slate-50 dark:bg-slate-800 p-2 rounded">
              Switch to "By Market" tab to edit individual market values
            </div>
          </TabsContent>
          
          <TabsContent value="markets" className="p-4 pt-3 space-y-3">
            {/* Show total at top of markets tab */}
            <div className="flex items-center justify-between p-2 bg-primary/10 rounded-lg mb-2">
              <span className="text-xs font-medium text-muted-foreground">Company Total:</span>
              <span className="text-lg font-bold text-primary">
                {format === 'currency' ? `$${companyTotal.toLocaleString()}` : companyTotal.toLocaleString()}
              </span>
            </div>
            
            {/* Loading indicator */}
            {loadingValues && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            
            {/* Market inputs */}
            {!loadingValues && (
              <div className="space-y-3 max-h-[250px] overflow-y-auto pr-1">
                {markets.map(market => (
                  <div key={market.slug} className="space-y-1">
                    <Label className="text-xs font-medium">{market.name}</Label>
                    <div className="relative">
                      {format === 'currency' && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      )}
                      <Input
                        type="number"
                        placeholder={`Enter ${label.toLowerCase()}`}
                        value={marketValues[market.slug] || ''}
                        onChange={(e) => handleMarketValueChange(market.slug, e.target.value)}
                        className={format === 'currency' ? 'pl-7' : ''}
                        data-testid={`edit-${metricKey}-${market.slug}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        <div className="p-4 border-t dark:border-slate-700 flex justify-between items-center">
          <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={loading || loadingValues} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {loading ? 'Saving...' : 'Save All'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// =====================================
// KPI CARD COMPONENT WITH TOOLTIP (Legacy - keeping for compatibility)
// =====================================
const KPICard = ({ label, kpi, format = 'number', onClick, isActive, tooltip }) => {
  if (!kpi) return null;
  
  const status = kpi.status || 'on_pace';
  const colors = statusColors[status] || statusColors.on_pace;
  
  const formatValue = (val) => {
    if (format === 'currency') return formatCurrency(val);
    if (format === 'percent') return formatPercent(val);
    if (format === 'multiple') return formatMultiple(val);
    return formatNumber(val);
  };
  
  const TrendIcon = kpi.trend === 'up' ? TrendingUp : kpi.trend === 'down' ? TrendingDown : Minus;
  
  // Get status description for 3-tier system
  const getStatusDescription = () => {
    if (status === 'on_pace') return 'Meeting or exceeding target';
    if (status === 'at_risk') return 'Above minimum but below target';
    if (status === 'below_min') return 'Below minimum threshold';
    return statusLabels[status] || 'Unknown';
  };
  
  return (
    <div
      onClick={onClick}
      data-testid={`kpi-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
      title={tooltip}
      className={`
        flex-shrink-0 min-w-[180px] p-4 rounded-lg border-l-4 cursor-pointer transition-all
        ${colors.border} ${colors.bg}
        ${isActive ? 'ring-2 ring-primary' : 'hover:scale-[1.02]'}
      `}
    >
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
        {label}
        {tooltip && (
          <span className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" title={tooltip}>
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
        )}
      </div>
      <div className="text-2xl font-bold dark:text-white">
        {formatValue(kpi.actual)}
      </div>
      
      {/* End-of-Month Projection - Leadership View */}
      {kpi.pace_projection !== undefined && kpi.target > 0 && (
        <div className="mt-2 p-2 rounded bg-slate-100 dark:bg-slate-800/50">
          <div className="text-xs text-muted-foreground">Projected EOM</div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-semibold">{formatValue(format === 'currency' ? Math.round(kpi.pace_projection * 100) / 100 : Math.round(kpi.pace_projection))}</span>
            <span className="text-xs text-muted-foreground">vs {formatValue(kpi.target)} target</span>
          </div>
          <div className={`text-xs mt-1 ${kpi.gap_to_target >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {kpi.gap_to_target >= 0 ? '↑' : '↓'} {Math.abs(Math.round(kpi.gap_to_target))} {kpi.gap_to_target >= 0 ? 'above' : 'below'} target
          </div>
        </div>
      )}
      
      {kpi.comparison_delta_percent !== null && kpi.comparison_delta_percent !== undefined && (
        <div className={`flex items-center gap-1 text-xs mt-1 ${kpi.comparison_delta_percent >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          <TrendIcon className="h-3 w-3" />
          {kpi.comparison_delta_percent >= 0 ? '+' : ''}{kpi.comparison_delta_percent.toFixed(1)}% vs prior
        </div>
      )}
      <div className="flex items-center gap-2 mt-2">
        <span className={`w-2 h-2 rounded-full ${colors.badge}`} />
        <span className={`text-xs ${colors.text}`} title={getStatusDescription()}>
          {statusLabels[status] || status}
        </span>
      </div>
    </div>
  );
};

// =====================================
// EXECUTIVE KPI STRIP (with inline editing)
// =====================================
const ExecutiveKPIStrip = ({ kpis, markets = [], period = 'mtd', onSaved, weeklyData = [], daysElapsed = 26, totalDays = 28 }) => {
  // Leadership-friendly labels - NO abbreviations
  // Order follows the sales funnel: Leads → Appointments → Issued → Demos → Sales → Payments
  const kpiConfig = [
    { key: 'leads', label: 'New Opportunities', format: 'number' },
    { key: 'appointments', label: 'Appointments Set', format: 'number' },
    { key: 'issued', label: 'Leads Issued', format: 'number' },
    { key: 'demos', label: 'Demos', format: 'number' },
    { key: 'sales', label: 'Jobs Sold', format: 'number' },
    { key: 'payments_received', label: 'Payments Received', format: 'number' },
    { key: 'revenue', label: 'Sold Jobs Amount', format: 'currency' },
    { key: 'revenue_collected', label: 'Revenue Amount', format: 'currency' },
    { key: 'spend', label: 'Ad Spend', format: 'currency' },
    { key: 'cpl', label: 'Cost/Lead', format: 'currency' },
    { key: 'roas', label: 'Return on Spend', format: 'multiple' }
  ];
  
  return (
    <div 
      data-testid="executive-kpi-strip"
      className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600"
    >
      {kpiConfig.map(({ key, label, format }) => (
        <EditableKPICard
          key={key}
          metricKey={key}
          label={label}
          kpi={kpis[key]}
          format={format}
          tooltip={kpiTooltips[key]}
          markets={markets}
          period={period}
          onSaved={onSaved}
          weeklyData={weeklyData}
          daysElapsed={daysElapsed}
          totalDays={totalDays}
        />
      ))}
    </div>
  );
};

// =====================================
// SCORECARD CELL
// =====================================
const ScorecardCell = ({ value, status, format = 'number', onClick }) => {
  const colors = statusColors[status || 'green'];
  
  const formatValue = (val) => {
    if (val === null || val === undefined) return '-';
    if (format === 'currency') return formatCurrency(val);
    if (format === 'percent') return formatPercent(val);
    if (format === 'multiple') return formatMultiple(val);
    return formatNumber(val);
  };
  
  return (
    <td 
      onClick={onClick}
      className={`
        px-3 py-2 text-center cursor-pointer transition-colors
        ${colors.bg} hover:brightness-95 dark:hover:brightness-110
      `}
    >
      <span className="font-medium dark:text-white">{formatValue(value)}</span>
    </td>
  );
};

// =====================================
// SCORECARD GRID
// =====================================
const ScorecardGrid = ({ data, onCellClick }) => {
  if (!data || !data.by_market || data.by_market.length === 0) {
    return (
      <Card className="dark:bg-slate-800/60">
        <CardHeader>
          <CardTitle className="text-lg">Market Scorecard</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No market data available for this period
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const columns = [
    { key: 'leads', label: 'Leads', format: 'number', dept: 'marketing' },
    { key: 'spend', label: 'Spend', format: 'currency', dept: 'marketing' },
    { key: 'cpl', label: 'CPL', format: 'currency', dept: 'marketing' },
    { key: 'appointments_set', label: 'Appts', format: 'number', dept: 'sales' },
    { key: 'sales', label: 'Sales', format: 'number', dept: 'sales' },
    { key: 'close_rate', label: 'Close %', format: 'percent', dept: 'sales' },
    { key: 'revenue', label: 'Revenue', format: 'currency', dept: 'finance' },
    { key: 'roas', label: 'ROAS', format: 'multiple', dept: 'finance' }
  ];
  
  const deptColors = {
    marketing: 'border-blue-500',
    sales: 'border-green-500',
    finance: 'border-purple-500'
  };
  
  // Add company totals row
  const companyRow = {
    market_id: 'company',
    market_name: 'COMPANY',
    kpis: data.kpis
  };
  
  const allRows = [companyRow, ...data.by_market];
  
  return (
    <Card data-testid="scorecard-grid" className="dark:bg-slate-800/60 overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          Market Scorecard
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b dark:border-slate-700">
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide w-32">
                  Market
                </th>
                {/* Department Headers */}
                <th colSpan="3" className={`px-2 py-1 text-center text-xs font-semibold uppercase tracking-wider border-b-2 ${deptColors.marketing} text-blue-600 dark:text-blue-400`}>
                  Marketing
                </th>
                <th colSpan="3" className={`px-2 py-1 text-center text-xs font-semibold uppercase tracking-wider border-b-2 ${deptColors.sales} text-green-600 dark:text-green-400`}>
                  Sales
                </th>
                <th colSpan="2" className={`px-2 py-1 text-center text-xs font-semibold uppercase tracking-wider border-b-2 ${deptColors.finance} text-purple-600 dark:text-purple-400`}>
                  Finance
                </th>
              </tr>
              <tr className="border-b dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                <th className="px-4 py-2"></th>
                {columns.map(col => (
                  <th key={col.key} className="px-3 py-2 text-xs font-medium text-muted-foreground text-center">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-700">
              {allRows.map((row, idx) => (
                <tr 
                  key={row.market_id}
                  className={`
                    ${idx === 0 ? 'bg-slate-100 dark:bg-slate-800 font-semibold' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}
                  `}
                >
                  <td className="px-4 py-3 text-sm font-medium dark:text-white whitespace-nowrap">
                    {row.market_name}
                  </td>
                  {columns.map(col => {
                    const kpi = row.kpis?.[col.key];
                    return (
                      <ScorecardCell
                        key={col.key}
                        value={kpi?.actual}
                        status={kpi?.status}
                        format={col.format}
                        onClick={() => onCellClick(col.key, row.market_id, row.market_name)}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// =====================================
// WEEKLY PACING TABLE
// =====================================
const WeeklyPacingTable = ({ weeklyPacing, byMarket, kpis, weeklyTargets }) => {
  if (!weeklyPacing || weeklyPacing.length === 0) {
    return null;
  }
  
  const markets = byMarket?.slice(0, 5) || [];
  const numWeeks = weeklyPacing.length;
  
  // Weekly targets from $25M annual target
  // 170 leads/week company-wide, 34 leads/week per market
  const companyWeeklyTarget = 170;
  const companyWeeklyMin = 136;
  const marketWeeklyTarget = 34;
  const marketWeeklyMin = 27;
  
  // Calculate running totals for leads
  let mtdLeads = 0;
  let mtdTarget = 0;
  const weeklyWithMtd = weeklyPacing.map((week, idx) => {
    mtdLeads += week.total_leads || 0;
    mtdTarget += companyWeeklyTarget;
    return { ...week, mtd_leads: mtdLeads, mtd_target: mtdTarget };
  });
  
  return (
    <Card data-testid="weekly-pacing-table" className="dark:bg-slate-800/60">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          Weekly Pacing - Leads
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Week</th>
                {markets.map(m => (
                  <th key={m.market_id} className="px-3 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                    {m.market_name?.split(' ')[0] || 'Unknown'}
                  </th>
                ))}
                <th className="px-3 py-3 text-right text-xs font-medium text-muted-foreground uppercase border-l dark:border-slate-600">Total</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-muted-foreground uppercase">MTD</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-700">
              {weeklyWithMtd.map((week, idx) => {
                // Determine row status based on weekly target
                const weekLeads = week.total_leads || 0;
                const isCurrentWeek = week.is_current_week;
                const weekStatus = isCurrentWeek 
                  ? 'current' 
                  : weekLeads >= companyWeeklyTarget 
                    ? 'goal_met' 
                    : weekLeads >= companyWeeklyMin 
                      ? 'needs_attention' 
                      : 'below_min';
                
                const rowClass = isCurrentWeek 
                  ? 'bg-blue-50 dark:bg-blue-900/20' 
                  : weekStatus === 'goal_met' 
                    ? 'bg-green-50/50 dark:bg-green-900/10' 
                    : weekStatus === 'needs_attention' 
                      ? 'bg-yellow-50/50 dark:bg-yellow-900/10' 
                      : weekStatus === 'below_min' 
                        ? 'bg-red-50/50 dark:bg-red-900/10' 
                        : '';
                
                return (
                  <tr key={week.week_number} className={rowClass}>
                    <td className="px-4 py-3 text-sm dark:text-white">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          Week {week.week_number}
                          {week.is_current_week && <span className="ml-1 text-xs text-blue-600 dark:text-blue-400">*</span>}
                        </span>
                        {!isCurrentWeek && weekStatus === 'goal_met' && (
                          <span className="text-xs bg-green-500/20 text-green-600 px-1.5 py-0.5 rounded">✓</span>
                        )}
                        {!isCurrentWeek && weekStatus === 'needs_attention' && (
                          <span className="text-xs bg-yellow-500/20 text-yellow-600 px-1.5 py-0.5 rounded">Min ✓</span>
                        )}
                        {!isCurrentWeek && weekStatus === 'below_min' && (
                          <span className="text-xs bg-red-500/20 text-red-600 px-1.5 py-0.5 rounded">Miss</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{week.week_start} - {week.week_end}</div>
                    </td>
                    {markets.map(m => {
                      const marketLeads = week.by_market?.[m.market_id]?.leads || 0;
                      const marketMet = marketLeads >= marketWeeklyTarget;
                      const marketMinMet = marketLeads >= marketWeeklyMin;
                      return (
                        <td key={m.market_id} className={`px-3 py-3 text-right text-sm ${
                          marketMet ? 'text-green-600 dark:text-green-400 font-medium' : 
                          marketMinMet ? 'text-yellow-600 dark:text-yellow-400' : 
                          'dark:text-white'
                        }`}>
                          {marketLeads}
                        </td>
                      );
                    })}
                    <td className={`px-3 py-3 text-right text-sm font-medium border-l dark:border-slate-600 ${
                      weekLeads >= companyWeeklyTarget ? 'text-green-600 dark:text-green-400' : 
                      weekLeads >= companyWeeklyMin ? 'text-yellow-600 dark:text-yellow-400' : 
                      'dark:text-white'
                    }`}>
                      {weekLeads}
                    </td>
                    <td className="px-3 py-3 text-right text-sm font-medium text-blue-600 dark:text-blue-400">
                      {week.mtd_leads}
                    </td>
                  </tr>
                );
              })}
              
              {/* Pace Row */}
              <tr className="bg-slate-100 dark:bg-slate-800 font-semibold border-t-2 dark:border-slate-600">
                <td className="px-4 py-3 text-sm">PACE</td>
                {markets.map(m => {
                  const mKpi = byMarket?.find(bm => bm.market_id === m.market_id)?.kpis?.leads;
                  return (
                    <td key={m.market_id} className="px-3 py-3 text-right text-sm">
                      {Math.round(mKpi?.pace_projection || 0)}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-right text-sm border-l dark:border-slate-600">
                  {Math.round(kpis?.leads?.pace_projection || 0)}
                </td>
                <td className="px-3 py-3"></td>
              </tr>
              
              {/* Weekly Target Row */}
              <tr className="bg-blue-50 dark:bg-blue-900/10">
                <td className="px-4 py-3 text-sm text-blue-700 dark:text-blue-300 font-medium">WEEKLY TARGET</td>
                {markets.map(m => (
                  <td key={m.market_id} className="px-3 py-3 text-right text-sm text-blue-700 dark:text-blue-300">
                    {marketWeeklyTarget}
                  </td>
                ))}
                <td className="px-3 py-3 text-right text-sm text-blue-700 dark:text-blue-300 border-l dark:border-slate-600 font-medium">
                  {companyWeeklyTarget}
                </td>
                <td className="px-3 py-3 text-right text-sm text-blue-700 dark:text-blue-300">
                  {companyWeeklyTarget * numWeeks}
                </td>
              </tr>
              
              {/* Weekly Minimum Row */}
              <tr className="bg-orange-50 dark:bg-orange-900/10">
                <td className="px-4 py-3 text-sm text-orange-700 dark:text-orange-300 font-medium">WEEKLY MINIMUM</td>
                {markets.map(m => (
                  <td key={m.market_id} className="px-3 py-3 text-right text-sm text-orange-700 dark:text-orange-300">
                    {marketWeeklyMin}
                  </td>
                ))}
                <td className="px-3 py-3 text-right text-sm text-orange-700 dark:text-orange-300 border-l dark:border-slate-600 font-medium">
                  {companyWeeklyMin}
                </td>
                <td className="px-3 py-3 text-right text-sm text-orange-700 dark:text-orange-300">
                  {companyWeeklyMin * numWeeks}
                </td>
              </tr>
              
              {/* Pace Row (Projected) */}
              <tr className="bg-slate-100 dark:bg-slate-800 font-semibold border-t-2 dark:border-slate-600">
                <td className="px-4 py-3 text-sm">PACE (Proj)</td>
                {markets.map(m => {
                  const mKpi = byMarket?.find(bm => bm.market_id === m.market_id)?.kpis?.leads;
                  return (
                    <td key={m.market_id} className="px-3 py-3 text-right text-sm">
                      {Math.round(mKpi?.pace_projection || 0)}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-right text-sm border-l dark:border-slate-600">
                  {Math.round(kpis?.leads?.pace_projection || 0)}
                </td>
                <td className="px-3 py-3"></td>
              </tr>
              
              {/* Monthly Target Row */}
              <tr className="bg-blue-100 dark:bg-blue-900/20">
                <td className="px-4 py-3 text-sm text-blue-700 dark:text-blue-300 font-medium">MONTHLY TARGET</td>
                {markets.map(m => {
                  const mKpi = byMarket?.find(bm => bm.market_id === m.market_id)?.kpis?.leads;
                  return (
                    <td key={m.market_id} className="px-3 py-3 text-right text-sm text-blue-700 dark:text-blue-300">
                      {Math.round(mKpi?.target || 149)}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-right text-sm text-blue-700 dark:text-blue-300 border-l dark:border-slate-600 font-medium">
                  {Math.round(kpis?.leads?.target || 745)}
                </td>
                <td className="px-3 py-3"></td>
              </tr>
              
              {/* Monthly Minimum Row */}
              <tr className="bg-orange-100 dark:bg-orange-900/20">
                <td className="px-4 py-3 text-sm text-orange-700 dark:text-orange-300 font-medium">MONTHLY MINIMUM</td>
                {markets.map(m => {
                  const mKpi = byMarket?.find(bm => bm.market_id === m.market_id)?.kpis?.leads;
                  return (
                    <td key={m.market_id} className="px-3 py-3 text-right text-sm text-orange-700 dark:text-orange-300">
                      {Math.round(mKpi?.minimum || 119)}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-right text-sm text-orange-700 dark:text-orange-300 border-l dark:border-slate-600 font-medium">
                  {Math.round(kpis?.leads?.minimum || 596)}
                </td>
                <td className="px-3 py-3"></td>
              </tr>
              
              {/* Projected End of Month Row - Leadership Clarity */}
              <tr className="bg-slate-100 dark:bg-slate-800/50">
                <td className="px-4 py-3 text-sm font-semibold">PROJECTED EOM</td>
                {markets.map(m => {
                  const mKpi = byMarket?.find(bm => bm.market_id === m.market_id)?.kpis?.leads;
                  const projection = Math.round(mKpi?.pace_projection || 0);
                  return (
                    <td key={m.market_id} className="px-3 py-3 text-right text-sm font-semibold">
                      {projection}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-right text-sm font-semibold border-l dark:border-slate-600">
                  {Math.round(kpis?.leads?.pace_projection || 0)}
                </td>
                <td className="px-3 py-3"></td>
              </tr>
              
              {/* Status vs Target Row */}
              <tr>
                <td className="px-4 py-3 text-sm font-medium">vs Target</td>
                {markets.map(m => {
                  const mKpi = byMarket?.find(bm => bm.market_id === m.market_id)?.kpis?.leads;
                  const delta = Math.round((mKpi?.pace_projection || 0) - (mKpi?.target || 149));
                  const isPositive = delta >= 0;
                  return (
                    <td key={m.market_id} className={`px-3 py-3 text-right text-sm font-medium ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {isPositive ? '+' : ''}{delta}
                    </td>
                  );
                })}
                <td className={`px-3 py-3 text-right text-sm font-medium border-l dark:border-slate-600 ${(kpis?.leads?.gap_to_target || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {Math.round(kpis?.leads?.gap_to_target || 0) >= 0 ? '+' : ''}{Math.round(kpis?.leads?.gap_to_target || 0)}
                </td>
                <td className="px-3 py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-xs text-muted-foreground border-t dark:border-slate-700">
          * Current week (partial data)
        </div>
      </CardContent>
    </Card>
  );
};

// =====================================
// DRILLDOWN DRAWER
// =====================================
const DrilldownDrawer = ({ isOpen, onClose, kpiKey, marketId, marketName, data }) => {
  const [drilldownData, setDrilldownData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && kpiKey) {
      fetchDrilldownData();
    }
  }, [isOpen, kpiKey, marketId]);
  
  const fetchDrilldownData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const params = new URLSearchParams({ kpi: kpiKey });
      if (marketId && marketId !== 'company') params.append('market_id', marketId);
      
      const response = await axios.get(`${API}/marketing/pacing/drilldown?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrilldownData(response.data);
    } catch (error) {
      console.error('Failed to fetch drilldown data:', error);
      toast.error('Failed to load details');
    } finally {
      setLoading(false);
    }
  };
  
  const kpiLabels = {
    leads: 'Leads',
    appointments_set: 'Appointments Set',
    set_rate: 'Set Rate',
    demo_rate: 'Demo Rate',
    close_rate: 'Close Rate',
    sales: 'Sales',
    revenue: 'Revenue',
    spend: 'Marketing Spend',
    cpl: 'Cost per Lead',
    cpas: 'Cost per Appointment',
    roas: 'Return on Ad Spend'
  };
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent data-testid="drilldown-drawer" className="w-[480px] sm:max-w-[480px] overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle className="flex items-center justify-between">
            <div>
              <span>{kpiLabels[kpiKey] || kpiKey}</span>
              {marketName && marketName !== 'COMPANY' && (
                <span className="ml-2 text-muted-foreground">- {marketName}</span>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={() => onClose(false)}>
              <X className="h-4 w-4" />
            </Button>
          </SheetTitle>
        </SheetHeader>
        
        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : drilldownData ? (
          <div className="space-y-6">
            {/* By Source Breakdown */}
            <div>
              <h4 className="text-sm font-medium mb-3">Breakdown by Source</h4>
              <div className="space-y-2">
                {drilldownData.by_source?.map(source => (
                  <div key={source.source} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <span className="font-medium">{source.source || 'Unknown'}</span>
                    <div className="flex gap-4 text-sm">
                      <span>Sales: {source.sales}</span>
                      <span>Revenue: {formatCurrency(source.revenue)}</span>
                      <span className={source.close_rate >= 30 ? 'text-green-600' : 'text-yellow-600'}>
                        {source.close_rate}%
                      </span>
                    </div>
                  </div>
                ))}
                {(!drilldownData.by_source || drilldownData.by_source.length === 0) && (
                  <div className="text-center py-4 text-muted-foreground">No source data available</div>
                )}
              </div>
            </div>
            
            {/* Trend Data */}
            {drilldownData.trend && drilldownData.trend.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-3">Daily Trend</h4>
                <div className="space-y-1">
                  {drilldownData.trend.slice(-7).map(item => (
                    <div key={item.date} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{item.date}</span>
                      <span className="font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Data Sources */}
            <div className="pt-4 border-t dark:border-slate-700">
              <h4 className="text-xs font-medium text-muted-foreground uppercase mb-2">Data Sources</h4>
              <div className="flex flex-wrap gap-2">
                {drilldownData.data_sources?.map(src => (
                  <Badge key={src} variant="outline" className="text-xs">
                    {src}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Select a KPI to view details
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// =====================================
// WEBSITE PERFORMANCE CARD (GA4 Connection)
// =====================================
const WebsitePerformanceCard = () => {
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gaConfig, setGaConfig] = useState({
    measurementId: '',
    propertyId: '',
    websiteUrl: ''
  });
  
  const handleConnect = async () => {
    if (!gaConfig.measurementId || !gaConfig.propertyId) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      // Save GA config to backend
      await axios.post(`${API}/integrations/google-analytics`, {
        measurement_id: gaConfig.measurementId,
        property_id: gaConfig.propertyId,
        website_url: gaConfig.websiteUrl
      });
      
      setIsConnected(true);
      setConnectModalOpen(false);
      toast.success('Google Analytics connected successfully!');
    } catch (error) {
      console.error('Failed to connect GA:', error);
      toast.error('Failed to connect. Please check your credentials.');
    } finally {
      setSaving(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };
  
  return (
    <>
      <Card className="dark:bg-slate-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="h-4 w-4 text-blue-500" />
            Website Performance
            <Badge 
              variant={isConnected ? 'default' : 'outline'} 
              className={`ml-auto text-xs ${isConnected ? 'bg-green-500' : ''}`}
            >
              {isConnected ? 'Connected' : 'Not Connected'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isConnected ? (
            <div className="text-center py-6 text-sm">
              <Check className="h-8 w-8 mx-auto mb-3 text-green-500" />
              <p className="font-medium text-slate-700 dark:text-slate-300 mb-1">Google Analytics Connected</p>
              <p className="text-muted-foreground mb-3 text-xs">
                Measurement ID: {gaConfig.measurementId}
              </p>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setConnectModalOpen(true)}>
                Update Settings
              </Button>
            </div>
          ) : (
            <div className="text-center py-6 text-sm">
              <Globe className="h-8 w-8 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
              <p className="font-medium text-slate-700 dark:text-slate-300 mb-1">Google Analytics Not Configured</p>
              <p className="text-muted-foreground mb-3 text-xs max-w-xs mx-auto">
                Connect GA4 to see website sessions, conversions, and traffic sources.
              </p>
              <div className="flex gap-2 justify-center">
                <Button 
                  variant="default" 
                  size="sm" 
                  className="text-xs"
                  onClick={() => setConnectModalOpen(true)}
                  data-testid="connect-ga-btn"
                >
                  <Link2 className="h-3 w-3 mr-1" />
                  Connect
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-xs"
                  onClick={() => setHelpOpen(true)}
                >
                  <HelpCircle className="h-3 w-3 mr-1" />
                  Help
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Connect GA4 Modal */}
      <Dialog open={connectModalOpen} onOpenChange={setConnectModalOpen}>
        <DialogContent className="max-w-lg" data-testid="ga-connect-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-500" />
              Connect Google Analytics
            </DialogTitle>
            <DialogDescription>
              Enter your GA4 credentials to connect your website analytics.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="measurementId" className="flex items-center gap-1">
                Measurement ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="measurementId"
                placeholder="G-XXXXXXXXXX"
                value={gaConfig.measurementId}
                onChange={(e) => setGaConfig(prev => ({ ...prev, measurementId: e.target.value }))}
                data-testid="ga-measurement-id-input"
              />
              <p className="text-xs text-muted-foreground">
                Found in GA4 → Admin → Data Streams → Select stream → Measurement ID
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="propertyId" className="flex items-center gap-1">
                Property ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="propertyId"
                placeholder="123456789"
                value={gaConfig.propertyId}
                onChange={(e) => setGaConfig(prev => ({ ...prev, propertyId: e.target.value }))}
                data-testid="ga-property-id-input"
              />
              <p className="text-xs text-muted-foreground">
                Found in GA4 → Admin → Property Settings → Property ID (numbers only)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="websiteUrl">Website URL (optional)</Label>
              <Input
                id="websiteUrl"
                placeholder="https://rhinoshield.com"
                value={gaConfig.websiteUrl}
                onChange={(e) => setGaConfig(prev => ({ ...prev, websiteUrl: e.target.value }))}
              />
            </div>
            
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs">
              <p className="font-medium text-blue-700 dark:text-blue-300 mb-1 flex items-center gap-1">
                <HelpCircle className="h-3 w-3" />
                Need help finding these values?
              </p>
              <p className="text-blue-600 dark:text-blue-400">
                Click the Help button for step-by-step WordPress instructions.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConnect} disabled={saving}>
              {saving ? 'Connecting...' : 'Connect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Help Modal - WordPress Instructions */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="ga-help-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-blue-500" />
              How to Connect Google Analytics (WordPress)
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="find" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="find">Find Your IDs</TabsTrigger>
              <TabsTrigger value="wordpress">WordPress Setup</TabsTrigger>
              <TabsTrigger value="verify">Verify Connection</TabsTrigger>
            </TabsList>
            
            <TabsContent value="find" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
                    Find Your Measurement ID
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-8">
                    <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">analytics.google.com</a></li>
                    <li>Click the <strong>gear icon</strong> (Admin) in the bottom left</li>
                    <li>Under &quot;Property&quot;, click <strong>Data Streams</strong></li>
                    <li>Click on your website stream</li>
                    <li>Copy the <strong>Measurement ID</strong> (starts with G-)</li>
                  </ol>
                  <div className="flex items-center gap-2 p-2 bg-slate-100 dark:bg-slate-800 rounded text-sm font-mono">
                    <span>Example: G-ABC123XYZ9</span>
                    <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => copyToClipboard('G-ABC123XYZ9')}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
                    Find Your Property ID
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-8">
                    <li>In GA4 Admin, click <strong>Property Settings</strong></li>
                    <li>Look for <strong>Property ID</strong> at the top</li>
                    <li>Copy the numbers (e.g., 123456789)</li>
                  </ol>
                  <div className="flex items-center gap-2 p-2 bg-slate-100 dark:bg-slate-800 rounded text-sm font-mono">
                    <span>Example: 123456789</span>
                    <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => copyToClipboard('123456789')}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="wordpress" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    <strong>Note:</strong> If GA4 is already installed on your WordPress site, you only need to enter the IDs above. The steps below are for first-time setup.
                  </p>
                </div>
                
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold">Option 1: Using Site Kit by Google (Recommended)</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-4">
                    <li>In WordPress, go to <strong>Plugins → Add New</strong></li>
                    <li>Search for <strong>&quot;Site Kit by Google&quot;</strong></li>
                    <li>Install and activate the plugin</li>
                    <li>Follow the setup wizard to connect your Google account</li>
                    <li>Select your GA4 property when prompted</li>
                  </ol>
                </div>
                
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold">Option 2: Manual Installation</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-4">
                    <li>Go to <strong>Appearance → Theme Editor</strong> (or use a header plugin)</li>
                    <li>Find <strong>header.php</strong></li>
                    <li>Paste the GA4 tracking code before the closing <code>&lt;/head&gt;</code> tag</li>
                    <li>Save the file</li>
                  </ol>
                  <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded text-xs font-mono overflow-x-auto">
                    <pre>{`<!-- Google tag (gtag.js) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'G-XXXXXXXXXX');
</script>`}</pre>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="verify" className="space-y-4 mt-4">
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold">Verify GA4 is Working</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-4">
                  <li>Open your website in a new browser tab</li>
                  <li>Go to GA4 → Reports → <strong>Realtime</strong></li>
                  <li>You should see yourself as an active user</li>
                  <li>If not, wait 24-48 hours for data to populate</li>
                </ol>
              </div>
              
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Once verified, enter your Measurement ID and Property ID in the Connect dialog above.
                </p>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHelpOpen(false)}>
              Close
            </Button>
            <Button onClick={() => { setHelpOpen(false); setConnectModalOpen(true); }}>
              <Link2 className="h-4 w-4 mr-2" />
              Connect Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// =====================================
// CALL ATTRIBUTION CARD (CallRail/Dialpad Connection)
// =====================================
const CallAttributionCard = () => {
  const [helpOpen, setHelpOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [callStats, setCallStats] = useState(null);
  
  // Fetch live stats on mount
  useEffect(() => {
    fetchLiveStats();
  }, []);
  
  const fetchLiveStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/call-center/live-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.connected) {
        setIsConnected(true);
        setCallStats(response.data);
      } else {
        setIsConnected(false);
      }
    } catch (error) {
      console.error('Failed to fetch call stats:', error);
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setSyncing(true);
    await fetchLiveStats();
    setSyncing(false);
    toast.success('Call data refreshed!');
  };
  
  if (loading) {
    return (
      <Card className="dark:bg-slate-800/60">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card className="dark:bg-slate-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Phone className="h-4 w-4 text-green-500" />
            Call Attribution
            <Badge 
              variant={isConnected ? 'default' : 'outline'} 
              className={`ml-auto text-xs ${isConnected ? 'bg-green-500' : ''}`}
            >
              {isConnected ? 'Live' : 'Not Connected'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isConnected && callStats ? (
            <div className="space-y-3">
              {/* Main Stats */}
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{callStats.total_calls?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Total Calls MTD</p>
                </div>
                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{callStats.human_answered?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Answered by Rep</p>
                </div>
              </div>
              
              {/* Breakdown - More accurate categories */}
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
                  <p className="font-bold text-amber-600">{callStats.system_handled?.toLocaleString() || 0}</p>
                  <p className="text-muted-foreground">IVR/System</p>
                </div>
                <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
                  <p className="font-bold text-purple-600">{callStats.voicemail?.toLocaleString() || 0}</p>
                  <p className="text-muted-foreground">Voicemail</p>
                </div>
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <p className="font-bold text-blue-600">{Math.floor((callStats.avg_duration_seconds || 0) / 60)}:{String((callStats.avg_duration_seconds || 0) % 60).padStart(2, '0')}</p>
                  <p className="text-muted-foreground">Avg Duration</p>
                </div>
              </div>
              
              {/* Human Answer Rate */}
              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded text-center">
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  {callStats.human_answer_rate}% answered by a rep
                </span>
              </div>
              
              {/* Today */}
              {callStats.today && (
                <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded text-xs">
                  <p className="font-medium mb-1">Today</p>
                  <div className="flex justify-between">
                    <span>{callStats.today.total} calls</span>
                    <span className="text-green-600">{callStats.today.human_answered} by rep</span>
                    <span className="text-slate-500">{callStats.today.other} other</span>
                  </div>
                </div>
              )}
              
              {/* Top Sources */}
              {callStats.top_sources && callStats.top_sources.length > 0 && (
                <div className="text-xs">
                  <p className="font-medium mb-1 text-muted-foreground">Top Sources</p>
                  <div className="space-y-1">
                    {callStats.top_sources.slice(0, 3).map((src, i) => (
                      <div key={i} className="flex justify-between items-center">
                        <span className="truncate max-w-[120px]">{src.name}</span>
                        <span className="font-medium">{src.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Refresh Button */}
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full text-xs"
                onClick={handleRefresh}
                disabled={syncing}
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Refreshing...' : 'Refresh Data'}
              </Button>
              
              <p className="text-[10px] text-muted-foreground text-center">
                {callStats.period_label} • via {callStats.provider}
              </p>
            </div>
          ) : (
            <div className="text-center py-6 text-sm">
              <Phone className="h-8 w-8 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
              <p className="font-medium text-slate-700 dark:text-slate-300 mb-1">Call Tracking Not Configured</p>
              <p className="text-muted-foreground mb-3 text-xs max-w-xs mx-auto">
                Connect CallRail to track call volume, sources, and conversions.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs"
                onClick={() => setHelpOpen(true)}
              >
                <HelpCircle className="h-3 w-3 mr-1" />
                Setup Guide
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Help Modal - CallRail Instructions */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="callrail-help-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-green-500" />
              How to Connect CallRail
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="callrail" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="callrail">CallRail Setup</TabsTrigger>
              <TabsTrigger value="dialpad">Dialpad Setup</TabsTrigger>
            </TabsList>
            
            <TabsContent value="callrail" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <span className="bg-green-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
                    Find Your Account ID
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-8">
                    <li>Log in to <a href="https://app.callrail.com" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:underline">app.callrail.com</a></li>
                    <li>Click <strong>Settings</strong> (gear icon) in the top right</li>
                    <li>Select <strong>API Access</strong> from the menu</li>
                    <li>Your <strong>Account ID</strong> is displayed at the top</li>
                  </ol>
                </div>
                
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <span className="bg-green-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
                    Get Your API Key
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-8">
                    <li>On the same API Access page</li>
                    <li>Click <strong>Create New Key</strong> if you don&apos;t have one</li>
                    <li>Give it a name like &quot;RHINO Integration&quot;</li>
                    <li>Copy the generated API key</li>
                  </ol>
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded text-xs text-amber-700 dark:text-amber-300">
                    <strong>Important:</strong> The API key is only shown once. Save it securely!
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <span className="bg-green-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
                    Enable Webhooks (Optional but Recommended)
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-8">
                    <li>In CallRail, go to <strong>Settings → Webhooks</strong></li>
                    <li>Click <strong>Add Webhook</strong></li>
                    <li>Set the URL to receive call notifications</li>
                    <li>Select events: <strong>Inbound Call</strong>, <strong>Outbound Call</strong>, <strong>SMS</strong></li>
                  </ol>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="dialpad" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
                    Access Dialpad Admin Portal
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-8">
                    <li>Log in to <a href="https://dialpad.com/app" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">dialpad.com/app</a></li>
                    <li>Click your profile icon → <strong>Admin Settings</strong></li>
                    <li>Navigate to <strong>Company Settings</strong></li>
                  </ol>
                </div>
                
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
                    Generate API Key
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-8">
                    <li>Go to <strong>Integrations</strong> section</li>
                    <li>Find <strong>API</strong> or <strong>Developer</strong> options</li>
                    <li>Generate a new API key for this integration</li>
                    <li>Copy your Account ID and API Key</li>
                  </ol>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHelpOpen(false)}>
              Close
            </Button>
            <Button onClick={() => { setHelpOpen(false); setConnectModalOpen(true); }} className="bg-green-600 hover:bg-green-700">
              <Link2 className="h-4 w-4 mr-2" />
              Connect Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// =====================================
// MAIN COMPONENT
// =====================================
export const OnTrackPacing = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [markets, setMarkets] = useState([]);
  
  // Realtime pacing data (from new endpoint)
  const [realtimePacingData, setRealtimePacingData] = useState(null);
  const [realtimeLoading, setRealtimeLoading] = useState(true);
  
  // Auth context
  const { user } = useAuth();
  
  // Filter state for main dashboard (legacy filters for scorecard/weekly pacing)
  const [period, setPeriod] = useState('mtd');
  const [compareTo, setCompareTo] = useState('prior_period');
  const [marketFilter, setMarketFilter] = useState('all');
  const [realTimePacing, setRealTimePacing] = useState(true);
  
  // NEW: Filter state for Targets & Pacing section
  const [targetsPeriod, setTargetsPeriod] = useState('mtd');
  const [targetsMarket, setTargetsMarket] = useState('all');
  const [targetsCustomDate, setTargetsCustomDate] = useState(null);
  
  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeKPI, setActiveKPI] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedMarketName, setSelectedMarketName] = useState(null);
  
  // Sync status
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [lastEnrichment, setLastEnrichment] = useState(null);
  const [trueUpModalOpen, setTrueUpModalOpen] = useState(false);
  
  // Targets editor modal
  const [targetsModalOpen, setTargetsModalOpen] = useState(false);
  
  // Section order for drag-and-drop reordering
  const [sectionOrder, setSectionOrder] = useState(() => getSavedSectionOrder());
  
  // Fetch realtime pacing data from new endpoint
  const fetchRealtimePacing = useCallback(async () => {
    setRealtimeLoading(true);
    try {
      // Build query params based on header filter state (shared across all sections)
      const params = new URLSearchParams({ period });
      
      // Add market filter if not 'all'
      if (marketFilter && marketFilter !== 'all') {
        params.append('market_id', marketFilter);
      }
      
      // Add custom date range if set (from Targets section)
      if (targetsCustomDate?.start && targetsCustomDate?.end) {
        params.set('period', 'custom');
        params.append('start_date_custom', targetsCustomDate.start);
        params.append('end_date_custom', targetsCustomDate.end);
      }
      
      const response = await axios.get(`${API}/marketing/pacing/realtime?${params}`);
      setRealtimePacingData(response.data);
    } catch (error) {
      console.error('Failed to fetch realtime pacing:', error);
      // Silent fail - realtime section will show empty state
    } finally {
      setRealtimeLoading(false);
    }
  }, [period, marketFilter, targetsCustomDate]);
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const params = new URLSearchParams({
        period,
        compare_to: compareTo,
        real_time_pacing: realTimePacing
      });
      
      if (marketFilter !== 'all') params.append('market_id', marketFilter);
      
      const response = await axios.get(`${API}/marketing/pacing/data?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      setLastSync(new Date());
    } catch (error) {
      console.error('Failed to fetch pacing data:', error);
      toast.error('Failed to load pacing data');
    } finally {
      setLoading(false);
    }
  }, [period, compareTo, marketFilter, realTimePacing]);
  
  const fetchFilters = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      if (!token) return; // Don't fetch if not authenticated
      
      const marketsRes = await axios.get(`${API}/markets`, { headers: { Authorization: `Bearer ${token}` } });
      setMarkets(marketsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Fetch realtime pacing data
  useEffect(() => {
    fetchRealtimePacing();
  }, [fetchRealtimePacing]);
  
  const handleTargetsUpdated = () => {
    fetchRealtimePacing();
  };
  
  const handleKPIClick = (kpiKey) => {
    setActiveKPI(kpiKey);
    setSelectedMarket(null);
    setSelectedMarketName(null);
    setDrawerOpen(true);
  };
  
  const handleCellClick = (kpiKey, marketId, marketName) => {
    setActiveKPI(kpiKey);
    setSelectedMarket(marketId);
    setSelectedMarketName(marketName);
    setDrawerOpen(true);
  };
  
  const handleRefresh = () => {
    setSyncing(true);
    fetchData().finally(() => setSyncing(false));
  };
  
  // Sync KPIs from Builder Prime - full automated sync
  const handleSyncFromBP = async () => {
    setEnriching(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      // Trigger KPI sync (runs in background)
      const response = await axios.post(
        `${API}/integrations/builderprime/sync/kpi-refresh?period=${period}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Builder Prime KPI sync started — data will refresh in ~2 minutes');
      
      // Poll for completion
      let pollCount = 0;
      const pollInterval = setInterval(async () => {
        pollCount++;
        try {
          const statusRes = await axios.get(
            `${API}/integrations/builderprime/sync/kpi-status`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const status = statusRes.data;
          
          if (status?.status === 'success' && status?.last_success_at) {
            const syncTime = new Date(status.last_success_at);
            // Check if this is a recent sync (within last 5 minutes)
            if (Date.now() - syncTime.getTime() < 300000) {
              clearInterval(pollInterval);
              setLastEnrichment(syncTime);
              toast.success(`KPI sync complete — ${status.last_result?.markets_written || 0} markets updated`);
              fetchRealtimePacing();
              fetchData();
              setEnriching(false);
            }
          }
          
          if (pollCount > 30) { // Stop after ~2.5 minutes
            clearInterval(pollInterval);
            setEnriching(false);
            toast.info('Sync still running in background — refresh page in a minute');
          }
        } catch (e) {
          // Ignore polling errors
        }
      }, 5000);
      
    } catch (error) {
      console.error('Failed to sync from BP:', error);
      toast.error('Failed to start Builder Prime sync');
      setEnriching(false);
    }
  };
  
  // Fetch last enrichment status on mount
  const fetchEnrichmentStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/integrations/builderprime/sync/status`);
      const lastRun = response.data?.financial_enrichment?.last_run;
      if (lastRun) {
        setLastEnrichment(new Date(lastRun));
      }
    } catch (error) {
      console.error('Failed to fetch enrichment status:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchEnrichmentStatus();
  }, [fetchEnrichmentStatus]);
  
  return (
    <Layout>
      <div data-testid="on-track-pacing-page" className="space-y-6">
        {/* Sticky Header */}
        <div className="sticky top-0 z-40 -mx-6 px-6 py-4 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b dark:border-slate-700">
          {/* Title Row */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold dark:text-white flex items-center gap-3">
                <BarChart3 className="h-7 w-7 text-primary" />
                On-Track Pacing
              </h1>
              <p className="text-muted-foreground text-sm mt-1">Marketing Performance Dashboard</p>
            </div>
            <div className="flex items-center gap-2 text-sm">
              {(() => {
                const minutesAgo = lastSync ? Math.round((Date.now() - lastSync.getTime()) / 60000) : null;
                const isStale = minutesAgo !== null && minutesAgo > 30;
                return (
                  <>
                    <span className={`w-2 h-2 rounded-full ${syncing ? 'bg-yellow-500 animate-pulse' : isStale ? 'bg-orange-500' : 'bg-green-500'}`} />
                    <span className={isStale ? 'text-orange-600 dark:text-orange-400' : 'text-muted-foreground'}>
                      {syncing ? 'Refreshing...' : 
                       lastSync ? (
                         minutesAgo === 0 ? 'Just updated' :
                         minutesAgo < 60 ? `Updated ${minutesAgo} min ago` :
                         `Updated ${Math.round(minutesAgo / 60)}h ago`
                       ) : 'Loading...'}
                      {isStale && ' (may be stale)'}
                    </span>
                  </>
                );
              })()}
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleRefresh} 
                disabled={syncing}
                title="Refresh data"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          
          {/* Filter Row */}
          <div className="flex flex-wrap items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mtd">MTD</SelectItem>
                <SelectItem value="qtd">QTD</SelectItem>
                <SelectItem value="ytd">YTD</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={compareTo} onValueChange={setCompareTo}>
              <SelectTrigger className="w-40" data-testid="compare-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="prior_period">vs Prior Period</SelectItem>
                <SelectItem value="prior_year">vs Prior Year</SelectItem>
                <SelectItem value="none">No Comparison</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={marketFilter} onValueChange={setMarketFilter}>
              <SelectTrigger className="w-40" data-testid="market-filter">
                <SelectValue placeholder="All Markets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Markets</SelectItem>
                {markets.map(m => (
                  <SelectItem key={m.id} value={m.slug || m.code?.toLowerCase() || m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2 ml-auto">
              {/* True-Up Button (Manual Override) */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setTrueUpModalOpen(true)}
                className="gap-2"
                data-testid="true-up-btn"
                title="Manually enter correct values from Builder Prime"
              >
                <Edit3 className="h-4 w-4" />
                True-Up
              </Button>
              
              {/* Sync from Builder Prime - Full KPI Sync */}
              <div className="flex items-center gap-1">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleSyncFromBP}
                  disabled={enriching}
                  className="gap-2"
                  data-testid="sync-bp-btn"
                  title={lastEnrichment 
                    ? `Last synced: ${lastEnrichment.toLocaleString()}` 
                    : 'Sync all KPI metrics from Builder Prime API'
                  }
                >
                  <RefreshCw className={`h-4 w-4 ${enriching ? 'animate-spin' : ''}`} />
                  {enriching ? 'Syncing BP...' : 'Sync from BP'}
                </Button>
                {lastEnrichment && (
                  <span className="text-xs text-muted-foreground whitespace-nowrap" title={lastEnrichment.toLocaleString()}>
                    {(() => {
                      const mins = Math.round((Date.now() - lastEnrichment.getTime()) / 60000);
                      if (mins < 1) return 'just now';
                      if (mins < 60) return `${mins}m ago`;
                      const hrs = Math.round(mins / 60);
                      if (hrs < 24) return `${hrs}h ago`;
                      return `${Math.round(hrs / 24)}d ago`;
                    })()}
                  </span>
                )}
              </div>
              
              {/* Company Targets Settings Button */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setTargetsModalOpen(true)}
                className="gap-2"
                data-testid="header-settings-btn"
              >
                <Settings className="h-4 w-4" />
                Company Targets
              </Button>
              
              <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />
              
              <Switch
                id="real-time"
                checked={realTimePacing}
                onCheckedChange={setRealTimePacing}
              />
              <Label htmlFor="real-time" className="text-sm cursor-pointer">
                Real-Time Pacing
              </Label>
            </div>
          </div>
          
          {/* Period Info */}
          {data?.period && (
            <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
              <span>{data.period.start_date} — {data.period.end_date}</span>
              <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                {data.period.elapsed_percent}% elapsed ({data.period.days_elapsed}/{data.period.days_total} days)
              </span>
            </div>
          )}
        </div>
        
        {/* Executive KPI Strip - Key metrics from Builder Prime (Click to edit) */}
        {realtimePacingData?.metrics && (
          <ExecutiveKPIStrip 
            kpis={realtimePacingData.metrics} 
            markets={markets}
            period={period}
            onSaved={() => {
              fetchRealtimePacing();
              fetchData();
            }}
            weeklyData={realtimePacingData?.weekly_pacing || []}
            daysElapsed={realtimePacingData?.period?.days_elapsed || 26}
            totalDays={realtimePacingData?.period?.days_total || 28}
          />
        )}
        
        {/* Main Content */}
        {loading ? (
          <div className="space-y-6">
            <div className="flex gap-3 overflow-hidden">
              {[1,2,3,4,5,6].map(i => (
                <Skeleton key={i} className="h-28 w-40 flex-shrink-0" />
              ))}
            </div>
            <Skeleton className="h-96 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : data ? (
          <>
            <DraggableSections sectionOrder={sectionOrder} onReorder={setSectionOrder}>
              {/* ========== DEPARTMENT SCORECARD SECTION (Collapsible) ========== */}
              <div data-section-id="department-scorecard">
                <DepartmentScorecardSection 
                  data={realtimePacingData}
                  loading={realtimeLoading}
                  weeksInMonth={4}
                  currentWeek={Math.ceil((realtimePacingData?.period?.days_elapsed || 7) / 7)}
                />
              </div>
              
              {/* ========== WEEKLY REQUIRED OUTPUT ENGINE ========== */}
              <div data-section-id="weekly-required-output">
                <WeeklyRequiredOutput 
                  data={realtimePacingData}
                  loading={realtimeLoading}
                />
              </div>
              
              {/* ========== MARKETING EFFICIENCY BLOCK ========== */}
              <div data-section-id="marketing-efficiency">
                <MarketingEfficiencyBlock 
                  data={realtimePacingData}
                  loading={realtimeLoading}
                />
              </div>
              
              {/* ========== MARKET PERFORMANCE BREAKDOWN ========== */}
              <div data-section-id="market-performance">
                <MarketPerformanceBreakdown 
                  data={realtimePacingData}
                  loading={realtimeLoading}
                />
              </div>
              
              {/* ========== ENHANCED WEEKLY PACING GRID ========== */}
              <div data-section-id="weekly-pacing-grid">
                <EnhancedWeeklyPacingGrid 
                  weeklyData={realtimePacingData?.weekly_pacing || data?.weekly_pacing}
                  mtdData={realtimePacingData}
                  loading={realtimeLoading}
                />
              </div>
              
              {/* ========== Website + Calls Integration Panels ========== */}
              <div data-section-id="integrations-panel">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <WebsitePerformanceCard />
                  <CallAttributionCard />
                </div>
              </div>
            </DraggableSections>
          </>
        ) : (
          <Card className="dark:bg-slate-800/60">
            <CardContent className="py-12 text-center">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <h3 className="text-lg font-medium mb-2 dark:text-white">No Marketing Data Yet</h3>
              <p className="text-muted-foreground mb-2 max-w-md mx-auto">
                This dashboard shows your marketing performance against goals. 
                To get started, you&apos;ll need to:
              </p>
              <ol className="text-sm text-muted-foreground mb-4 text-left max-w-xs mx-auto space-y-1">
                <li>1. Set your monthly goals in Settings</li>
                <li>2. Import marketing data (spend, leads, sales)</li>
                <li>3. Or connect your agency API</li>
              </ol>
              <Button onClick={() => window.location.href = '/marketing-hub/settings'}>
                <Settings className="h-4 w-4 mr-2" />
                Configure Goals & Import Data
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Drilldown Drawer */}
        <DrilldownDrawer
          isOpen={drawerOpen}
          onClose={setDrawerOpen}
          kpiKey={activeKPI}
          marketId={selectedMarket}
          marketName={selectedMarketName}
          data={data}
        />
      </div>
      
      {/* Company Targets Editor Modal */}
      <MonthlyTargetsEditor
        isOpen={targetsModalOpen}
        onClose={() => setTargetsModalOpen(false)}
        onSave={() => {
          setTargetsModalOpen(false);
          handleRefresh();
        }}
        user={user}
        canEdit={true}
        selectedMarket={marketFilter}
        markets={markets}
        realtimeMetrics={realtimePacingData?.metrics}
      />
      
      {/* True-Up Modal for Manual Metric Overrides */}
      <MetricsTrueUpModal
        open={trueUpModalOpen}
        onOpenChange={setTrueUpModalOpen}
        currentMetrics={realtimePacingData?.metrics}
        period={period}
        market={marketFilter !== 'all' ? marketFilter : null}
        onSaved={() => {
          fetchRealtimePacing();
          fetchData();
        }}
      />
    </Layout>
  );
};

export default OnTrackPacing;
